import React, { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Header from './components/header';
import Footer from './components/footer';
import Applications from './components/applications';
import TermsOfUse from './pages/terms-of-use/TermsOfUse';
import VirtualAgent from './components/VirtualAgent';
import ChatWithTheo from './components/ChatBot/ChatWithTheo';
import Logout from './pages/logout/Logout';
import ApplicationV2 from './components/application/applicationV2';
import { DEFAULT_PAGE_SIZE, initialAppCriteria, initialHeaderLinks } from './shared/consts';
import { useAppConfig } from './context/app-configs-context';
import { AppActions, AppCriteria, AppResultInfo, CriteriaConstraint, CustomLink, PageInfo } from './types/decisions';
import { rankApps, rerankApps, rerankManyApps } from './api/decisions.api';
import { getCriteriaValueByType, transformCriteriaData } from './utils';
import './app.scss';

function App() {
    const {
        appConfigs: {
            show: {
                headerMenu,
                menuItems,
                header,
                footer,
            },
            criteriaSettings: {
                zipcode,
            },
        },
    } = useAppConfig();
    const [ headerLinks, setHeaderLinks ] = useState<CustomLink[]>(initialHeaderLinks);
    const [ externalItems, setExternalItems ] = useState<CustomLink[]>([]);

    const [ resultLoading, setResultLoading ] = useState<boolean>(true);
    const initialCriteriaData = useRef<AppCriteria>(initialAppCriteria);
    const [ criteriaData, setCriteriaData ] = useState<AppCriteria>(initialAppCriteria);
    const [ criteriaConstraints, setCriteriaConstraints ] = useState<{
        [key: string]: CriteriaConstraint[]
    }>({});
    const [ results, setResults ] = useState<AppResultInfo[]>([]);
    const [ rankMode, setRankMode ] = useState<'regular' | 'multi'>('regular');
    const [ selectedProductIds, setSelectedProductIds ] = useState<AppResultInfo['id'][]>([]);
    const [ oldSelectedProductIds, setOldSelectedProductIds ] = useState<AppResultInfo['id'][]>([]);
    const [ showSelectedItems, setShowSelectedItems ] = useState(true);
    const [ pageInfo, setPageInfo ] = useState<PageInfo>({
        page_size: DEFAULT_PAGE_SIZE,
        current_page: 1,
        start: 0,
        end: 0,
        total: 0,
        total_pages: 1,
    });

    const setSelectedProductIdsWrapper = (value: SetStateAction<string[]>) => {
        setSelectedProductIds(value);
        setShowSelectedItems(true);
    };

    const onCriteriaApply = useCallback((options = { scrollToTop: true }) => {
        if (options.scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }

        setResultLoading(true);
        rankApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            pageInfo.page_size,
            1,
        )
        .then(response => {
            setRankMode('regular');
            setShowSelectedItems(true);
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setResults(response.results);
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, zipcode ]);

    const onRerank = useCallback((rerankProductId: string, scrollToTop: boolean = false) => {
        if (scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }
        setResultLoading(true);
        rerankApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            rerankProductId,
            pageInfo.page_size, 1,
        ).then(response => {
            setResults(response.results);
            setShowSelectedItems(true);
            setRankMode('regular');
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setCriteriaData(criteriaData => (
                {
                    ...criteriaData,
                    fields: criteriaData.fields.map(field => (
                        {
                            ...field,
                            importance: response.criteria[field.id],
                        }
                    )),
                }
            ));
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
         .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, zipcode ]);

    const onRerankMany = useCallback((scrollToTop: boolean = false) => {
        if (scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }
        setResultLoading(true);
        rerankManyApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            selectedProductIds,
            pageInfo.page_size,
            1,
        )
        .then(response => {
            setRankMode('multi');
            setOldSelectedProductIds(selectedProductIds);
            setResults(response.results);
            setShowSelectedItems(false);
            setCriteriaData(criteriaData => (
                {
                    ...criteriaData,
                    fields: criteriaData.fields.map(field => (
                        {
                            ...field,
                            importance: response.criteria[field.id],
                        }
                    )),
                }
            ));
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, selectedProductIds, zipcode ]);

    const onResetTradeOffs = useCallback(() => {
        if (JSON.stringify(criteriaData.fields) === JSON.stringify(initialCriteriaData.current.fields)) {
            return;
        }

        setCriteriaData({ ...initialCriteriaData.current });
        setResultLoading(true);

        rankApps(
            transformCriteriaData(
                initialCriteriaData.current,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            pageInfo.page_size,
            pageInfo.current_page,
        )
        .then(response => {
            setRankMode('regular');
            setShowSelectedItems(true);
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setResults(response.results);
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, initialCriteriaData, pageInfo, zipcode ]);

    const onSliderChange = (newValue: number, id: string) => {
        setCriteriaData(criteriaData => {
            const fields = criteriaData.fields;
            const changingFieldIdx = fields.findIndex(field => field.id === id);
            return {
                ...criteriaData,
                fields: [
                    ...fields.slice(0, changingFieldIdx),
                    {
                        ...fields[changingFieldIdx],
                        importance: newValue,
                    },
                    ...fields.slice(changingFieldIdx + 1),
                ],
            };
        });
    };

    const onSliderBulkChange = (newValues: {
        [key: string]: number
    }) => {
        setCriteriaData(criteriaData => (
            {
                ...criteriaData,
                fields: criteriaData.fields.map(field => {
                    const newFieldValue = newValues[field.id];

                    if (!newFieldValue) {
                        return {
                            ...field,
                            importance: 0,
                        };
                    }

                    return {
                        ...field,
                        importance: newFieldValue,
                    };
                }),
            }
        ));
    };

    const onTradeoffValueChange = (newValue: number | string, id: string, key?: string) => {
        setCriteriaData(criteriaData => {
            const fields = criteriaData.fields;
            const idx = criteriaData.fields.findIndex(field => field.id === id);
            const newVal = getCriteriaValueByType(fields[idx], newValue, key);

            return {
                ...criteriaData,
                fields: [
                    ...fields.slice(0, idx),
                    {
                        ...fields[idx],
                        val: newVal,
                    },
                    ...fields.slice(idx + 1),
                ],
            };
        });
    };

    const onPageChange = (page: number) => {
        setResultLoading(true);
        if (rankMode === 'regular') {
            rankApps(
                transformCriteriaData(
                    criteriaData,
                    criteriaConstraints,
                    { isZipSingleField: zipcode === 'SingleField' },
                ),
                pageInfo.page_size,
                page,
            )
            .then(response => {
                setResults(response.results);
                setShowSelectedItems(true);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
            })
            .finally(() => setResultLoading(false));
        } else {
            rerankManyApps(
                transformCriteriaData(
                    criteriaData,
                    criteriaConstraints,
                    { isZipSingleField: zipcode === 'SingleField' },
                ),
                oldSelectedProductIds,
                pageInfo.page_size,
                page,
            )
            .then(response => {
                setResults(response.results);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
            })
            .finally(() => setResultLoading(false));
        }
    };

    const onActionClick = useCallback((action: AppActions) => {
        switch (action) {
            case 'DECIDE':
                return onCriteriaApply();
            case 'IMPORTANCE':
                return console.info('Open Importance');
            case 'SETTINGS':
                return console.info('Open SETTINGS');
            case 'RESET':
                return onResetTradeOffs();
        }
    }, [ onCriteriaApply, onResetTradeOffs ]);

    useEffect(() => {
        setExternalItems(menuItems.externalItems);

        if (!headerMenu) {
            return;
        }

        setHeaderLinks([ ...menuItems.additionalItems, ...initialHeaderLinks ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ headerMenu, menuItems.additionalItems, menuItems.externalItems ]);

    return (
        <div className="app">
            <Router>
                {header ? (
                    <Header
                        showDecide={headerMenu && menuItems.decide}
                        showReset={headerMenu && menuItems.reset}
                        headerLinks={headerLinks}
                        externalItems={externalItems}
                        onActionClick={onActionClick}
                    />
                ) : null}
                <Routes>
                    <Route path="/" element={<Applications/>}/>
                    <Route path="/apps/:id" element={
                        <ApplicationV2
                            initialCriteriaData={initialCriteriaData}
                            criteriaData={criteriaData}
                            criteriaConstraints={criteriaConstraints}
                            setCriteriaConstraints={setCriteriaConstraints}
                            setCriteriaData={setCriteriaData}
                            onRerank={onRerank}
                            onRerankMany={onRerankMany}
                            onPageChange={onPageChange}
                            pageInfo={pageInfo}
                            onTradeoffValueChange={onTradeoffValueChange}
                            onSliderChange={onSliderChange}
                            results={results}
                            resultLoading={resultLoading}
                            setResultLoading={setResultLoading}
                            onCriteriaApply={onCriteriaApply}
                            onResetTradeOffs={onResetTradeOffs}
                            onSliderBulkChange={onSliderBulkChange}
                            setPageInfo={setPageInfo}
                            setResults={setResults}
                            selectedProductIds={showSelectedItems ? selectedProductIds : []}
                            setSelectedProductIds={setSelectedProductIdsWrapper}
                        />
                    }/>
                    <Route path="/terms-of-use" element={<TermsOfUse/>}/>
                    <Route path="/virtual-agent/:id" element={<VirtualAgent/>}/>
                    <Route path="/chat-theo/:id" element={<ChatWithTheo />} />
                    <Route path="/logout" element={<Logout/>}/>
                    <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>
                {footer ? <Footer/> : null}
            </Router>
        </div>
    );
}

export default App;
